import { api } from "@/api/client";

// Shapes mirror Api::V1::CollectionsController / Collections::BacklogService.

export type CollectionsKind = "deposit" | "sop" | "final";
export type InvoiceStatus = "open" | "paid" | "uncollectible" | "void" | "none" | "unknown";

export interface LastReminder {
  sent_at: string;
  sent_by: string;
}

export interface LatestComment {
  author: string;
  at: string | null;
  text: string;
}

export interface CollectionsRow {
  project_id: string;
  task_id: string;
  project_name: string | null;
  sunrise_url: string;
  region_id: string | null;
  region: string | null;
  lender: string | null;
  stage: string | null;
  bucket: string;
  task_age_days: number | null;
  invoice_status: InvoiceStatus;
  paid: boolean;
  invoice_url: string | null;
  invoice_number: string | null;
  amount_remaining: number | null;
  open_invoice_count: number;
  other_open_count: number;
  other_open_amount: number;
  comment_count: number | null;
  latest_comment: LatestComment | null;
  last_reminder: LastReminder | null;
  // Deposits only.
  down_payment?: number | null;
  // SOP only.
  install_date?: string | null;
  install_job_type?: string | null;
  install_job_status?: string | null;
  projected_install_date?: string | null;
  scheduled_by?: "task" | "date" | null;
}

export interface CollectionsBacklog {
  kind: CollectionsKind;
  region: string;
  summary: Record<string, number>;
  rows: CollectionsRow[];
  stripe_configured: boolean;
  as_of: string | null;
  loading: boolean;
  refreshing: boolean;
  // The last rebuild failed (cleared by the next good one). With as_of null the
  // list has never been built, so there is nothing to show yet.
  build_error: { message: string; at: string } | null;
  can_send_reminder: boolean;
  can_create_invoice: boolean;
}

export interface CommentItem {
  id: string | null;
  author: string;
  at: string | null;
  text: string;
}

export interface CommentThread extends CommentItem {
  replies: CommentItem[];
}

export interface TaskComments {
  task_id: string;
  count: number;
  threads: CommentThread[];
}

export async function fetchBacklog(kind: CollectionsKind, region: string): Promise<CollectionsBacklog> {
  const params: Record<string, string> = { kind };
  if (region !== "All") params.region = region;
  const { data } = await api.get<CollectionsBacklog>("/collections/backlog", { params });
  return data;
}

export async function fetchTaskComments(taskId: string, kind: CollectionsKind): Promise<TaskComments> {
  const { data } = await api.get<TaskComments>("/collections/task_comments", { params: { task_id: taskId, kind } });
  return data;
}

export async function sendReminder(projectId: string, kind: CollectionsKind): Promise<{ last_reminder: LastReminder }> {
  const { data } = await api.post("/collections/reminder", { project_id: projectId, kind });
  return data;
}

export async function createInvoice(projectId: string, kind: CollectionsKind): Promise<{ hosted_invoice_url: string | null }> {
  const { data } = await api.post("/collections/invoice", { project_id: projectId, kind });
  return data;
}
